import type { AiReviewStatus } from "@pericles/agent-orchestrator";

import type {
  CreateProjectSpecSectionInput,
  ProjectSpecSectionDetail,
  ProjectSpecSectionSummary,
  UpdateProjectSpecSectionInput,
} from "../types/specs";

interface ListResponse {
  ok?: boolean;
  items?: ProjectSpecSectionSummary[];
  error?: string;
}

interface SectionResponse {
  ok?: boolean;
  section?: ProjectSpecSectionDetail;
  error?: string;
}

export async function fetchProjectSpecSections(
  projectId: string,
): Promise<ProjectSpecSectionSummary[]> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections`);

  const payload = (await response.json()) as ListResponse;

  if (!response.ok || payload.ok === false) {
    throw new Error(payload.error ?? `Failed to load spec sections (status ${response.status})`);
  }

  return payload.items ?? [];
}

export async function createProjectSpecSection(
  projectId: string,
  input: CreateProjectSpecSectionInput,
): Promise<ProjectSpecSectionDetail> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as SectionResponse;

  if (!response.ok || payload.ok === false || !payload.section) {
    throw new Error(payload.error ?? `Failed to create spec section (status ${response.status})`);
  }

  return payload.section;
}

export async function updateProjectSpecSection(
  projectId: string,
  specSectionId: string,
  input: UpdateProjectSpecSectionInput,
): Promise<ProjectSpecSectionDetail> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections/${specSectionId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as SectionResponse;

  if (!response.ok || payload.ok === false || !payload.section) {
    throw new Error(payload.error ?? `Failed to update spec section (status ${response.status})`);
  }

  return payload.section;
}

export async function updateProjectSpecSectionAiReviewStatus(
  projectId: string,
  specSectionId: string,
  aiReviewStatus: AiReviewStatus,
): Promise<ProjectSpecSectionDetail> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections/${specSectionId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ aiReviewStatus }),
  });

  const payload = (await response.json()) as SectionResponse;

  if (!response.ok || payload.ok === false || !payload.section) {
    throw new Error(payload.error ?? `Failed to update spec section (status ${response.status})`);
  }

  return payload.section;
}

export async function deleteProjectSpecSection(
  projectId: string,
  specSectionId: string,
): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections/${specSectionId}`, {
    method: "DELETE",
  });

  if (response.status === 204) return;

  const payload = ((await response.json().catch(() => ({}))) ?? {}) as { error?: string };

  if (!response.ok) {
    throw new Error(payload.error ?? `Failed to delete spec section (status ${response.status})`);
  }
}

export async function suggestDslForSpecSection(
  projectId: string,
  specSectionId: string,
): Promise<ProjectSpecSectionDetail> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections/${specSectionId}/dsl/suggest`, {
    method: "POST",
  });

  const payload = (await response.json()) as SectionResponse;

  if (!response.ok || payload.ok === false || !payload.section) {
    throw new Error(payload.error ?? `Failed to suggest DSL fragments (status ${response.status})`);
  }

  return payload.section;
}

export async function acceptDslFragmentForSpecSection(
  projectId: string,
  specSectionId: string,
  fragment: unknown,
): Promise<ProjectSpecSectionDetail> {
  const response = await fetch(`/api/projects/${projectId}/spec-sections/${specSectionId}/dsl/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fragment }),
  });

  const payload = (await response.json()) as SectionResponse;

  if (!response.ok || payload.ok === false || !payload.section) {
    throw new Error(payload.error ?? `Failed to accept DSL fragment (status ${response.status})`);
  }

  return payload.section;
}
